import { RowOrigin, SourceKey } from '../models/source-key.model'
import { ROW_ID_FIELD } from '../models/transactions-query.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { unwrapFields } from './field-value'

/**
 * Maps a read-API row onto the internal {@link WorkbenchRow}.
 *
 * The API returns exactly the fields that were asked for, and those field names are the catalog's
 * `field` values, so the bulk of the mapping is a copy into `fields` where the ColDef factory's
 * value getters already look. Each value arrives wrapped in a `{ value, currency? }` envelope,
 * which {@link unwrapFields} strips so the rest of the panel sees scalars.
 *
 * Everything the contract does NOT pin down is isolated here so it is cheap to correct:
 *  - The per-counterpart match columns (`custodyMatches` / `custodyMatchesIds`, ...; helios !499)
 *    are carried through raw in {@link WorkbenchRow.fields} and interpreted by `grid/match-info.ts`
 *    for {@link MatchingService}.
 *  - Custody vs Trading for a Prometheus row is inferred from `extTranSource`; the contract does not
 *    say which field carries it.
 *  - Break counts have no read endpoint at all, so every row starts at zero.
 */

/** Status strings the API may use for `matchStatus`, normalised case-insensitively. */
const IGNORED_STATUS = new Set(['ignored', 'ignore'])
const MATCHED_STATUS = new Set(['matched', 'match'])
/**
 * `HELD` is the confirmed string: `ExternalTransactionStatus` in
 * `libs/common-api/src/lib/models/external-transaction.model.ts` and `ExtTranStatus.HELD` in helios
 * !498. `HOLD` has no evidence anywhere and is mapped purely as cheap insurance — the flex read
 * projection hardcodes `matchStatus: "UNMATCHED"` today, so nothing has yet exercised either
 * spelling end to end.
 */
const HELD_STATUS = new Set(['held', 'hold'])

const text = (value: unknown): string | undefined =>
  value == null ? undefined : String(value)

/**
 * Read the response's `availableActions` into a plain string list.
 *
 * Returns `undefined` when the field is absent or null — meaning "the server did not answer", which
 * leaves the menu ungated (see {@link WorkbenchRow.availableActions}). An empty array is preserved
 * as an empty array, because that IS an answer: nothing is permitted on this row.
 *
 * Accepts the array itself, a JSON string wrapping one, and a comma-separated string, for the same
 * reason `match-info.ts` does: these columns arrive through a generic field map, and a column that
 * comes back as text rather than JSON should degrade to the right list, not to one nonsense entry.
 */
const readAvailableActions = (value: unknown): string[] | undefined => {
  if (value == null) return undefined

  let source = value
  if (typeof source === 'string') {
    const trimmed = source.trim()
    if (!trimmed) return []
    if (trimmed.startsWith('[')) {
      try {
        source = JSON.parse(trimmed)
      } catch {
        // Not JSON after all — read it as a delimited list below.
        source = trimmed.split(',')
      }
    } else {
      source = trimmed.split(',')
    }
  }

  if (!Array.isArray(source)) return undefined

  return source
    .map(entry => (entry == null ? '' : String(entry).trim()))
    .filter(entry => entry !== '')
}

/**
 * Prometheus rows are either Custody or Trading, which drives the Source column and match
 * counting. `extTranSource` is the only returned field that looks like it carries the distinction;
 * anything unrecognised falls back to Custody rather than dropping the row.
 */
const prometheusOrigin = (fields: Record<string, unknown>): RowOrigin => {
  const source = String(fields['extTranSource'] ?? '').toLowerCase()
  if (source.includes('trad')) return 'trad'
  return 'cust'
}

const originOf = (
  sourceKey: SourceKey,
  fields: Record<string, unknown>,
): RowOrigin => {
  if (sourceKey === 'api') return 'api'
  if (sourceKey === 'cw') return 'cw'
  return prometheusOrigin(fields)
}

/**
 * Prometheus resolves dynamic keys out of `aggInfo`; keep the blob so runtime discovery still works.
 *
 * Exported for `services/http-transaction-matches.service.ts`, whose elements carry the same
 * `aggInfo` field and must parse it identically (an object, a JSON string, or null).
 */
export const aggregateInfoOf = (
  row: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  const blob = row['aggInfo']
  if (blob == null) return undefined
  if (typeof blob === 'object') return blob as Record<string, unknown>
  try {
    const parsed = JSON.parse(String(blob))
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : undefined
  } catch {
    return undefined
  }
}

export function rowFromApi(
  sourceKey: SourceKey,
  row: Record<string, unknown>,
  index: number,
): WorkbenchRow {
  const fields = unwrapFields(row)
  const idField = ROW_ID_FIELD[sourceKey as 'api' | 'prom']
  // Fall back to the page index so a row missing its identity field still renders and stays
  // addressable, rather than colliding on an empty id with every other such row. The fallback is
  // flagged, because it is NOT a server id and must never reach the actions endpoint
  // (`utils/action-ids.ts`).
  const serverId = text(fields[idField])
  const rowId = serverId ?? `${sourceKey}:${index}`
  const status = String(fields['matchStatus'] ?? '').toLowerCase()

  return {
    rowId,
    syntheticRowId: serverId == null ? true : undefined,
    origin: originOf(sourceKey, fields),
    // No local group is seeded from the response, and none is needed: the per-counterpart match
    // columns ARE the grouping, indexed by `MatchingService` rather than reinvented client-side. `group`
    // stays undefined on every fetched row.
    group: undefined,
    serverStatus: MATCHED_STATUS.has(status)
      ? 'matched'
      : IGNORED_STATUS.has(status)
      ? 'ignored'
      : HELD_STATUS.has(status)
      ? 'held'
      : undefined,
    ignored: IGNORED_STATUS.has(status),
    availableActions: readAvailableActions(fields['availableActions']),
    // No breaks endpoint exists; the Breaks column renders empty until one does.
    breaks: { high: 0, medium: 0 },
    accountId: text(fields['accountId']) ?? '',
    sourceLabel: text(fields['externalReferenceSource']),
    sec: text(fields['secIdentifier'] ?? fields['description']),
    aggregateInfo: aggregateInfoOf(row),
    fields,
  }
}

/** Map a whole page of read-API rows for one source. */
export function rowsFromApi(
  sourceKey: SourceKey,
  data: Array<Record<string, unknown>>,
): WorkbenchRow[] {
  return data.map((row, index) => rowFromApi(sourceKey, row, index))
}
